/**
 * LEAN THEME - Cart Notes
 * Auto-save, character counting, accordion toggle, gift option, and sync.
 */
'use strict';

const LeanCartNotes = (() => {
  const DEBOUNCE_MS = 500;
  const SAVED_DISPLAY_MS = 2000;
  const NEAR_LIMIT_THRESHOLD = 0.9;

  let saveTimers = {};
  let eventsBound = false;

  /**
   * Debounced save of cart note via AJAX
   */
  const saveNote = (value, context) => {
    clearTimeout(saveTimers[context]);
    saveTimers[context] = setTimeout(async () => {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: value })
        });

        if (!response.ok) throw new Error('Save failed');

        showSavedFeedback(context);
        syncNoteFields(value, context);
      } catch (err) {
        console.error('Cart note save error:', err);
      }
    }, DEBOUNCE_MS);
  };

  /**
   * Save gift wrap attribute via AJAX
   */
  const saveGiftWrap = async (checked, context) => {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { gift_wrap: checked ? 'yes' : '' }
        })
      });

      if (!response.ok) throw new Error('Gift save failed');

      // Sync other gift checkboxes
      document.querySelectorAll('[data-cart-notes-gift]').forEach(cb => {
        if (cb.closest('[data-cart-notes]')?.dataset.context !== context) {
          cb.checked = checked;
        }
      });
    } catch (err) {
      console.error('Gift wrap save error:', err);
    }
  };

  /**
   * Show "Saved" feedback on the relevant cart-notes block
   */
  const showSavedFeedback = (context) => {
    const container = document.querySelector(`[data-cart-notes][data-context="${context}"]`);
    const saved = container?.querySelector('[data-cart-notes-saved]');
    if (!saved) return;

    saved.hidden = false;
    saved.classList.add('is-visible');

    setTimeout(() => {
      saved.classList.remove('is-visible');
      setTimeout(() => { saved.hidden = true; }, 200);
    }, SAVED_DISPLAY_MS);
  };

  /**
   * Sync note text to other instances (drawer <-> page)
   */
  const syncNoteFields = (value, sourceContext) => {
    document.querySelectorAll('[data-cart-notes-input]').forEach(textarea => {
      const ctx = textarea.closest('[data-cart-notes]')?.dataset.context;
      if (ctx !== sourceContext) {
        textarea.value = value;
        updateCounter(textarea);
      }
    });
  };

  /**
   * Update character counter for a textarea
   */
  const updateCounter = (textarea) => {
    const container = textarea.closest('[data-cart-notes]');
    if (!container) return;

    const limit = parseInt(container.dataset.charLimit) || 0;
    if (limit === 0) return;

    const countEl = container.querySelector('[data-cart-notes-count]');
    const counterEl = container.querySelector('[data-cart-notes-counter]');
    if (!countEl) return;

    const len = textarea.value.length;
    countEl.textContent = len;

    if (counterEl) {
      counterEl.classList.toggle('is-near-limit', len >= limit * NEAR_LIMIT_THRESHOLD);
    }
  };

  /**
   * Toggle accordion
   */
  const toggleAccordion = (button) => {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    const targetId = button.getAttribute('aria-controls');
    const body = document.getElementById(targetId);

    button.setAttribute('aria-expanded', String(!expanded));
    if (body) {
      body.hidden = expanded;
    }
  };

  /**
   * Bind all events
   */
  const bindEvents = () => {
    if (eventsBound) return;
    eventsBound = true;

    // Textarea input -> auto-save (drawer only) + counter
    document.addEventListener('input', (e) => {
      const textarea = e.target.closest('[data-cart-notes-input]');
      if (!textarea) return;

      const container = textarea.closest('[data-cart-notes]');
      const context = container?.dataset.context;

      updateCounter(textarea);

      // Only auto-save for drawer context; page uses form submit
      if (context === 'drawer') {
        saveNote(textarea.value, context);
      }
    });

    // Accordion toggle
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-cart-notes-toggle]');
      if (toggle) {
        toggleAccordion(toggle);
      }
    });

    // Gift wrap checkbox
    document.addEventListener('change', (e) => {
      const gift = e.target.closest('[data-cart-notes-gift]');
      if (!gift) return;

      const context = gift.closest('[data-cart-notes]')?.dataset.context;
      // Always AJAX-save gift, even on page (it's a cart attribute, not part of form note)
      saveGiftWrap(gift.checked, context);
    });
  };

  /**
   * Initialize counters for pre-filled notes
   */
  const initCounters = () => {
    document.querySelectorAll('[data-cart-notes-input]').forEach(updateCounter);
  };

  const init = () => {
    bindEvents();
    initCounters();
  };

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init, saveNote, saveGiftWrap };
})();

window.LeanCartNotes = LeanCartNotes;
